import { toFullName } from '@repo/toolkit/to-full-name'
import Button from '@repo/ui/button'
import DataTable from '@repo/ui/data-table'
import { AddUserIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation, useQuery } from '@tanstack/react-query'
import { toast } from 'sonner'

import { addExternalStaffsToMission } from '../../../../api/missions/add-external-staffs-to-mission'
import { cancelExternalStaff } from '../../../../api/missions/cancel-external-staff'
import { confirmExternalStaff } from '../../../../api/missions/confirm-external-staff'
import {
  excludeBudgetToExternalStaff,
  includeBudgetToExternalStaff,
  updateBudgetToExternalStaff
} from '../../../../api/missions/external-staff-mission-actions'
import { getAffectedExternalStaffs } from '../../../../api/missions/get-affected-external-staffs'
import { useDataManager } from '../../../../lib/hooks/use-data-manager'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { MissionStaffStatusEnum } from '../../../../lib/types/enums/mission/mission-staff-status.enum'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import ActionTableWrapper from '../../../elements/action-table-wrapper'
import ExternalStaffForm from '../forms/external-staff-form'
import MissionStaffCancellationModal from '../mission-staff-cancellation-modal'
import MissionStaffConfirmationModal from '../mission-staff-confirmation-modal'
import { MissionStaff } from './mission-staff-table'
import { affectedListColumns, AffectedUser, mapExternalStaffToAffectedUser } from './utils'
type Props = MissionStaff

const AffectedExternalStaffList: React.FC<Props> = ({ missionId, mode = 'edit' }) => {
  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'add' | 'delete' | 'confirm',
    AffectedUser
  >(['add', 'delete', 'confirm'])

  const { data, setData, updateFields, removeItem, addItem } = useDataManager<AffectedUser>([])

  const { isPending } = useQuery({
    queryKey: ['mission-enrolled-staffs', missionId],
    queryFn: async () => {
      const res = await getAffectedExternalStaffs(+missionId)
      setData(res.map(mapExternalStaffToAffectedUser))
      return res
    }
  })

  const { mutate: confirmExternalStaffMutation, isPending: isConfirmExternalStaffPending } =
    useMutation({
      mutationFn: confirmExternalStaff,
      onError: showAxiosError,
      onSuccess: () => {
        closeModel('confirm')
        if (!selectedData) return
        const index = data?.findIndex((item) => item.id === selectedData.id)
        updateFields(index, [{ key: 'status', value: MissionStaffStatusEnum.AFFECTED }])
      }
    })

  const { mutate: cancelExternalStaffMutation, isPending: isCancelExternalStaffPending } =
    useMutation({
      mutationFn: cancelExternalStaff,
      onError: showAxiosError,
      onSuccess: () => {
        closeModel('delete')
        if (!selectedData) return
        removeItem(data?.findIndex((item) => item.id === selectedData.id))
      }
    })

  const {
    mutateAsync: addExternalStaffsToMissionMutation,
    isPending: isAddExternalStaffsToMissionPending
  } = useMutation({
    mutationFn: addExternalStaffsToMission,
    onError: showAxiosError,
    onSuccess: (data) => {
      closeModel('add')
      addItem(mapExternalStaffToAffectedUser(data))
    }
  })

  const columns = affectedListColumns(missionId, mode, {
    includeBudget: async (params) => {
      await includeBudgetToExternalStaff({
        externalStaffId: params.id,
        id: missionId
      })
      toast.success('Budget inclus avec succès')
    },
    excludeBudget: async (params) => {
      await excludeBudgetToExternalStaff({
        externalStaffId: params.id,
        id: missionId
      })
      toast.success('Budget exclus avec succès')
    },
    updateBudget: async (params) => {
      await updateBudgetToExternalStaff({
        externalStaffId: params.id,
        ...params,
        id: missionId
      })
      toast.success('Budget mis à jour avec succès')
    },
    updateFields,
    onConfirm: (item) => toggleModal('confirm', item),
    onCancel: (item) => toggleModal('delete', item)
  })
    .filter((column) => column.version.includes('EXTERNAL'))
    .filter((column) => column.visible !== false)

  if (isPending) return <Loader center />
  if (mode === 'view' && data?.length === 0) return null
  return (
    <ActionTableWrapper
      title={'Encadrants externes'}
      actions={[
        mode === 'edit' ? (
          <Button
            key={'add'}
            type="button"
            icon={AddUserIcon}
            label={'Ajouter un encadrant'}
            variant={'secondary'}
            onClick={() => toggleModal('add')}
          />
        ) : null
      ]}
    >
      <DataTable<AffectedUser>
        columns={columns}
        data={data ?? []}
        paginated={false}
        rowCount={data?.length ?? 0}
      />
      <Modal
        isOpen={modalState.add}
        size="medium"
        icon={AddUserIcon}
        title="Ajouter un encadrant"
        isLoading={isPending}
        onCancel={() => toggleModal('add')}
        hideActions
      >
        <ExternalStaffForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            isLoading: isAddExternalStaffsToMissionPending,
            disabled: isAddExternalStaffsToMissionPending,
            onCancel: () => toggleModal('add'),
            onConfirm: async (data) => {
              await addExternalStaffsToMissionMutation({ id: missionId, ...data })
            }
          })}
        />
      </Modal>
      {selectedData && (
        <MissionStaffCancellationModal
          onCancel={() => toggleModal('delete')}
          isOpen={modalState.delete}
          isLoading={isCancelExternalStaffPending}
          onConfirm={() =>
            cancelExternalStaffMutation({ id: missionId, externalStaffId: selectedData.id })
          }
        />
      )}

      {selectedData && (
        <MissionStaffConfirmationModal
          onConfirm={() =>
            confirmExternalStaffMutation({ id: missionId, externalStaffId: selectedData.id })
          }
          onCancel={() => toggleModal('confirm')}
          isOpen={modalState.confirm}
          isLoading={isConfirmExternalStaffPending}
          name={toFullName(selectedData.firstName, selectedData.lastName)}
        />
      )}
    </ActionTableWrapper>
  )
}

export default AffectedExternalStaffList
